import React from "react";
import type { DocumentInfoVariant, FileType, ICitation } from "@/api";

// Panel lifecycle states
export type DocumentViewerStatus =
    | "idle"
    | "loading"
    | "ready"
    | "error"
    // A non-PDF citation resolved: the document exists but has no in-app
    // preview (spreadsheet). The @react-pdf-viewer must NOT mount — the
    // SpreadsheetViewerPanel renders instead. Set by DocumentViewerProvider's
    // guard, keyed off the fetched `docInfo.file_type`.
    | "unsupported";

export interface OutputDocumentDownloadCapability {
    documentId: number;
}

export type OutputDocumentOrigin = "template-render-card" | "report-card";

/** Opens a source page; never grants the output Download. */
export type SourceDocumentOrigin = "report-link";

export interface DocumentViewerOpenContext {
    origin?: OutputDocumentOrigin | SourceDocumentOrigin;
    signal?: AbortSignal;
    target?: "replace" | "new-tab";
    variant?: DocumentInfoVariant;
    /** When the variant read answers 404, put back the tabs from before the open and re-read the document itself into the variant's slot. */
    fallbackToDefault?: boolean;
}

export function isOutputDocumentOrigin(
    origin: string | undefined
): origin is OutputDocumentOrigin {
    return origin === "template-render-card" || origin === "report-card";
}

export type DocumentViewerOpenOutcome =
    | { type: "opened" }
    | { type: "not-found" }
    | { type: "error" }
    | { type: "superseded" };

export interface DocumentViewerTab {
    id: string;
    documentId: number;
    /** With `documentId`, the tab's identity: the same document in another variant is another tab. */
    variant?: DocumentInfoVariant;
    openedWith: Pick<DocumentViewerOpenContext, "origin"> & {
        /** Identify the accepted outside open, independently of activation reads. */
        requestId: number;
    };
    openMode: "open" | "activate";
    fileType: FileType | null;
    selectedSource: ICitation | null; // full citation: URL, highlights, pages
    status: Exclude<DocumentViewerStatus, "idle">;
    initialPage: number | undefined; // 1-indexed; undefined = start of doc
    currentPage: number;
    outputDownload: OutputDocumentDownloadCapability | null;
}

export interface DocumentViewerState {
    tabs: DocumentViewerTab[];
    activeTabId: string | null;
}

export function isSpreadsheet(fileType: FileType | null): boolean {
    return fileType === "xlsx" || fileType === "csv";
}

export function projectActiveTab(
    state: DocumentViewerState
): Pick<
    DocumentViewerContextValue,
    | "selectedSource"
    | "status"
    | "initialPage"
    | "outputDownload"
    | "isOpen"
    | "isTabActivation"
> {
    const active = state.tabs.find((tab) => tab.id === state.activeTabId);
    return {
        selectedSource:
            active?.status === "error"
                ? null
                : (active?.selectedSource ?? null),
        status: active?.status ?? "idle",
        initialPage: active?.initialPage,
        outputDownload: active?.outputDownload ?? null,
        isOpen: state.tabs.length > 0,
        isTabActivation: active?.openMode === "activate",
    };
}

export function nextActiveTabId(
    tabs: readonly Pick<DocumentViewerTab, "id">[],
    activeTabId: string | null,
    closedId: string
): string | null {
    const index = tabs.findIndex((tab) => tab.id === closedId);
    if (index === -1 || activeTabId !== closedId) return activeTabId;
    return tabs[index - 1]?.id ?? tabs[index + 1]?.id ?? null;
}

export interface DocumentViewerContextValue extends DocumentViewerState {
    isTabActivation?: boolean;
    selectedSource: ICitation | null;
    status: DocumentViewerStatus;
    initialPage: number | undefined;
    outputDownload: OutputDocumentDownloadCapability | null;
    isOpen: boolean;
    openDocument(source: ICitation, page?: number): Promise<void>;
    openDocumentById(
        documentId: number,
        page?: number,
        context?: DocumentViewerOpenContext
    ): Promise<DocumentViewerOpenOutcome>;
    // Close panel and reset all state to idle
    closeDocument(): void;
    activateTab(id: string): Promise<void>;
    closeTab(id: string): string | null;
    /** Remember an active tab's one-indexed page without publishing a context update. */
    setTabPage(id: string, page: number): void;
    /**
     * Keep a ready PDF tab's loaded bytes, so activating the tab again shows them without a read or download.
     * `readData` runs only when the tab would take the bytes.
     */
    holdTabPdf?(
        id: string,
        documentUrl: string,
        readData: () => Promise<Uint8Array>
    ): void;
    // Ref-based imperative channel registered by DocumentViewerPanel on mount.
    // Used for same-document page navigation WITHOUT remounting the @react-pdf-viewer Viewer.
    // Why a ref (not state): this is a command channel, not reactive data.
    // The Provider creates it; DocumentViewerPanel registers its pageNavigationPlugin.jumpToPage here.
    jumpToPageRef: React.MutableRefObject<((page: number) => void) | null>;
}
